import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { SchemaOrg, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { LocaleKey } from "@/i18n";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";

export default function TermsAndConditions() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  
  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home', 'Home'), url: baseUrl },
    { name: t('terms.breadcrumb'), url: getLocalizedUrl(locale as LocaleKey, '/terminos-y-condiciones') }
  ]);

  const langCode = locale === 'pt-BR' ? 'pt-BR' : locale === 'en' || locale === 'en-EU' ? 'en' : 'es';
  
  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);

  const termsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": t('terms.pageTitle'),
    "description": t('terms.pageDescription'),
    "url": getLocalizedUrl(locale as LocaleKey, '/terminos-y-condiciones'),
    "inLanguage": langCode,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Fora da Rota",
      "url": baseUrl
    },
    "about": {
      "@type": "Thing",
      "name": t('terms.breadcrumb')
    },
    "lastReviewed": "2025-03-31"
  };

  return (
    <div className="min-h-screen bg-background">
      <HreflangTags path="/terminos-y-condiciones" />
<SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, termsSchema]} />
      
      <Navbar />
      
      <main id="main-content">
        <LegalPageLayout 
          title={t('terms.title')} 
          lastUpdate={t('terms.lastUpdate')}
        >
          <div className="space-y-4 mb-10">
            <p className="text-muted-foreground leading-relaxed">{t('terms.intro1')}</p>
            <p className="text-muted-foreground leading-relaxed">{t('terms.intro2')}</p>
          </div>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section1Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('terms.section1Intro')}</p>
            <ul className="text-muted-foreground space-y-2 list-disc pl-6">
              <li>{t('terms.section1_1')}</li>
              <li>{t('terms.section1_2')}</li>
              <li>{t('terms.section1_3')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section2Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('terms.section2_1Intro')}</p>
            <ul className="text-muted-foreground space-y-2 list-disc pl-6 mb-4">
              <li>{t('terms.section2_1a')}</li>
              <li>{t('terms.section2_1b')}</li>
              <li>{t('terms.section2_1c')}</li>
            </ul>
            <p className="text-muted-foreground mb-2">{t('terms.section2_2')}</p>
            <p className="text-muted-foreground">{t('terms.section2_3')}</p>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section3Title')}</h2>
            <div className="space-y-3 text-muted-foreground">
              <p>{t('terms.section3_1')}</p>
              <p>{t('terms.section3_2')}</p>
              <p>{t('terms.section3_3')}</p>
            </div>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section4Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('terms.section4_1Intro')}</p>
            <ul className="text-muted-foreground space-y-2 list-[lower-alpha] pl-6">
              <li>{t('terms.section4_1a')}</li>
              <li>{t('terms.section4_1b')}</li>
              <li>{t('terms.section4_1c')}</li>
              <li>{t('terms.section4_1d')}</li>
              <li>{t('terms.section4_1e')}</li>
              <li>{t('terms.section4_1f')}</li>
              <li>{t('terms.section4_1g')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section5Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('terms.section5_1Intro')}</p>
            <ul className="text-muted-foreground space-y-2 list-[lower-alpha] pl-6">
              <li>{t('terms.section5_1a')}</li>
              <li>{t('terms.section5_1b')}</li>
              <li>{t('terms.section5_1c')}</li>
              <li>{t('terms.section5_1d')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section6Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section6_1')}</li>
              <li>{t('terms.section6_2')}</li>
              <li>{t('terms.section6_3')}</li>
              <li>{t('terms.section6_4')}</li>
              <li>{t('terms.section6_5')}</li>
              <li>{t('terms.section6_6')}</li>
              <li>{t('terms.section6_7')}</li>
              <li>{t('terms.section6_8')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section7Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section7_1')}</li>
              <li>{t('terms.section7_2')}</li>
              <li>{t('terms.section7_3')}</li>
              <li>{t('terms.section7_4')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section8Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section8_1')}</li>
              <li>{t('terms.section8_2')}</li>
              <li>{t('terms.section8_3')}</li>
              <li>{t('terms.section8_4')}</li>
              <li>{t('terms.section8_5')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section9Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section9_1')}</li>
              <li>{t('terms.section9_2')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section10Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section10_1')}</li>
              <li>{t('terms.section10_2')}</li>
              <li>{t('terms.section10_3')}</li>
              <li>{t('terms.section10_4')}</li>
              <li>{t('terms.section10_5')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section11Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section11_1')}</li>
              <li>{t('terms.section11_2')}</li>
              <li>{t('terms.section11_3')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section12Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section12_1')}</li>
              <li>{t('terms.section12_2')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section13Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section13_1')}</li>
              <li>{t('terms.section13_2')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('terms.section14Title')}</h2>
            <ul className="text-muted-foreground space-y-3">
              <li>{t('terms.section14_1')}</li>
              <li>{t('terms.section14_2')}</li>
              <li>{t('terms.section14_3')}</li>
            </ul>
          </section>
        </LegalPageLayout>
      </main>
      
      <Footer />
    </div>
  );
}